import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Button, Card, CardSubtitle, CardTitle, Field, Input } from '@keobook/ui';
import { api, ApiError } from '../lib/api';
import { useMoneyFormatter } from '../lib/useCurrency';
import { Layout } from '../components/Layout';

type Period = Awaited<ReturnType<typeof api.listPeriods>>['periods'][number];
type CloseResult = Awaited<ReturnType<typeof api.closePeriod>>;

const STATUS_LABEL: Record<Period['status'], string> = {
  open: 'Open',
  in_review: 'In review',
  closed: 'Closed',
  locked: 'Locked',
};
const STATUS_TONE: Record<Period['status'], string> = {
  open: 'kb-pill--neutral',
  in_review: 'kb-pill--warn',
  closed: 'kb-pill--ok',
  locked: 'kb-pill--warn',
};

function monthLabel(start: string): string {
  const [y, m] = start.split('-');
  const d = new Date(Date.UTC(Number(y), Number(m) - 1, 1));
  return d.toLocaleString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export function Periods() {
  const fmt = useMoneyFormatter();
  const [periods, setPeriods] = useState<Period[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [lastClose, setLastClose] = useState<CloseResult | null>(null);
  const [reopenFor, setReopenFor] = useState<string | null>(null);
  const [reopenReason, setReopenReason] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api.listPeriods();
      setPeriods(r.periods);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load periods.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function onClose(p: Period) {
    setBusy(p.id);
    setError(null);
    setNotice(null);
    try {
      const res = await api.closePeriod(p.id);
      setLastClose(res);
      setNotice(
        `Closed ${monthLabel(p.periodStart)} — net income ${fmt(res.netIncomeCents)}.`,
      );
      await load();
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        const problem = err.problem as { title?: string };
        setError(problem.title ?? `Close failed (${err.status}).`);
      } else {
        setError(err instanceof Error ? err.message : 'Close failed.');
      }
    } finally {
      setBusy(null);
    }
  }

  async function onReopen(e: FormEvent) {
    e.preventDefault();
    if (!reopenFor || reopenReason.trim().length === 0) return;
    setBusy(reopenFor);
    setError(null);
    try {
      await api.reopenPeriod(reopenFor, reopenReason.trim());
      setNotice('Period reopened.');
      setReopenFor(null);
      setReopenReason('');
      setLastClose(null);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Reopen failed.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <Layout>
      <div className="kb-page" style={{ maxWidth: 820, margin: '0 auto', padding: '2rem 1.5rem' }}>
        <h1
          style={{
            fontSize: 26,
            fontWeight: 600,
            color: 'var(--kb-gray-900)',
            margin: '0 0 0.5rem',
          }}
        >
          Periods
        </h1>
        <p className="kb-muted" style={{ marginTop: 0, marginBottom: '1.5rem' }}>
          Close a month once the trial balance balances. Closing snapshots your books for that
          period — the numbers cannot change without an explicit reopen.
        </p>

        {error && (
          <div role="alert" className="kb-form-error">
            {error}
          </div>
        )}
        {notice && (
          <div className="kb-form-notice" role="status">
            {notice}
          </div>
        )}

        <Card>
          <CardTitle>Accounting periods</CardTitle>
          {loading ? (
            <CardSubtitle>Loading…</CardSubtitle>
          ) : periods.length === 0 ? (
            <CardSubtitle>
              Your first period will appear here once you (or the AI) post a journal entry.
            </CardSubtitle>
          ) : (
            <table className="kb-report" style={{ marginTop: '0.75rem' }}>
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Entries</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {periods.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <strong>{monthLabel(p.periodStart)}</strong>
                      <div className="kb-muted" style={{ fontSize: 11 }}>
                        {p.periodStart} → {p.periodEnd}
                      </div>
                    </td>
                    <td>{p.entryCount}</td>
                    <td>
                      <span className={`kb-pill ${STATUS_TONE[p.status]}`}>
                        {STATUS_LABEL[p.status]}
                      </span>
                      {p.closedAt && (
                        <div className="kb-muted" style={{ fontSize: 11 }}>
                          {new Date(p.closedAt).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="kb-report__amt">
                      {p.status === 'open' && (
                        <Button
                          size="sm"
                          disabled={busy === p.id || p.entryCount === 0}
                          onClick={() => onClose(p)}
                        >
                          {busy === p.id ? 'Closing…' : 'Close month'}
                        </Button>
                      )}
                      {p.status === 'closed' && (
                        <button
                          type="button"
                          className="kb-linkbtn"
                          onClick={() => {
                            setReopenFor(p.id);
                            setReopenReason('');
                          }}
                        >
                          Reopen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {reopenFor && (
          <div style={{ marginTop: '1.25rem' }}>
            <Card>
              <CardTitle>Reopen this period</CardTitle>
              <CardSubtitle>
                Reopening is recorded with the reason — Brickstone and the audit trail can see it.
              </CardSubtitle>
              <form onSubmit={onReopen} style={{ marginTop: '0.75rem' }}>
                <Field label="Reason" required>
                  <Input
                    value={reopenReason}
                    onChange={(e) => setReopenReason(e.currentTarget.value)}
                    placeholder="A receipt arrived late, fixing a coding mistake, etc."
                    required
                  />
                </Field>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Button
                    type="submit"
                    disabled={busy === reopenFor || reopenReason.trim().length === 0}
                  >
                    {busy === reopenFor ? 'Reopening…' : 'Reopen'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setReopenFor(null);
                      setReopenReason('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}

        {lastClose && (
          <div style={{ marginTop: '1.25rem' }}>
            <Card>
              <CardTitle>Close snapshot — Income Statement</CardTitle>
              <CardSubtitle>
                {lastClose.snapshots.incomeStatement.from} →{' '}
                {lastClose.snapshots.incomeStatement.to}
              </CardSubtitle>
              <table className="kb-report">
                <tbody>
                  <tr className="kb-report__subtotal">
                    <td>Total revenue</td>
                    <td className="kb-report__amt">
                      {fmt(lastClose.snapshots.incomeStatement.totalRevenueCents)}
                    </td>
                  </tr>
                  <tr className="kb-report__subtotal">
                    <td>Total expenses</td>
                    <td className="kb-report__amt">
                      {fmt(lastClose.snapshots.incomeStatement.totalExpensesCents)}
                    </td>
                  </tr>
                  <tr className="kb-report__total">
                    <td>Net income</td>
                    <td className="kb-report__amt">{fmt(lastClose.netIncomeCents)}</td>
                  </tr>
                </tbody>
              </table>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}
